import { useEffect, useMemo, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import { fetchAdminProjectsCreateScope } from '@frontend/api-clients/dashboardClient';
import { getEcosystems } from '@frontend/api-clients/ecosystemsClient';
import { fetchProjectsForEcosystem } from '@frontend/api-clients/dashboardClient';
import type { EcosystemDefinition, EcosystemProject, User } from '@frontend/types';
import { canSeeProjectAdmin, isPatriotAdmin, parseEcosystemAdminScopes } from '@frontend/utils/adminHubAccess';
import { useAdminWidgetExpanded } from './hooks/useAdminWidgetExpanded';
import ProjectAdminScopePicker, {
  type ProjectAdminScopeOption,
} from './project/components/ProjectAdminScopePicker';
import ProjectIdentityPanel from './project/components/ProjectIdentityPanel';
import ProjectMembershipPanel from './project/components/ProjectMembershipPanel';

interface ProjectAdminPageProps {
  user: User | null;
  /** When true, rail eligibility already established (maintainer without eco-admin). */
  allowMaintainerAccess?: boolean;
}

export default function ProjectAdminPage({
  user,
  allowMaintainerAccess = false,
}: ProjectAdminPageProps) {
  const [ecosystemDefs, setEcosystemDefs] = useState<EcosystemDefinition[]>([]);
  const [projectsByEco, setProjectsByEco] = useState<Record<string, EcosystemProject[]>>({});
  const [selectedKey, setSelectedKey] = useState('');
  const [eligible, setEligible] = useState(allowMaintainerAccess);
  const { expanded, toggleWidget } = useAdminWidgetExpanded(user, 'project', {
    identity: false,
    memberships: true,
  });

  const allowed = canSeeProjectAdmin(user, eligible || allowMaintainerAccess);

  useEffect(() => {
    if (!user) return;
    const token = authClient.getToken();
    if (!token) return;

    const load = async () => {
      try {
        const scope = (await fetchAdminProjectsCreateScope(token)) as {
          eligible?: boolean;
          ecosystems?: Array<{ id: string }>;
        };
        setEligible(Boolean(scope.eligible) || allowMaintainerAccess);

        const defs = await getEcosystems(token);
        setEcosystemDefs(defs);

        const patriot = isPatriotAdmin(user);
        const ecoScopes = parseEcosystemAdminScopes(user);
        const ecoIds = patriot || user.allowAllEcosystemAdmin
          ? defs.map((d) => d.id)
          : defs.map((d) => d.id).filter((id) => ecoScopes.has(id));

        // Also load accessible ecosystems so maintainers see their projects.
        const overviewEcoIds =
          ecoIds.length > 0 ? ecoIds : defs.map((d) => d.id);

          const loads = await Promise.all(
          overviewEcoIds.map(async (ecoId) => {
            try {
              const projects = await fetchProjectsForEcosystem(ecoId, token);
              return [ecoId, projects] as [string, EcosystemProject[]];
            } catch {
              return [ecoId, [] as EcosystemProject[]] as [string, EcosystemProject[]];
            }
          })
        );
        const next: Record<string, EcosystemProject[]> = {};
        for (const [ecoId, projects] of loads) next[ecoId] = projects;
        setProjectsByEco(next);
      } catch {
        setEligible(allowMaintainerAccess);
      }
    };
    void load();
  }, [user, allowMaintainerAccess]);

  const options: ProjectAdminScopeOption[] = useMemo(() => {
    const rows: ProjectAdminScopeOption[] = [];
    for (const [ecosystemId, projects] of Object.entries(projectsByEco)) {
      const ecoLabel =
        ecosystemDefs.find((e) => e.id === ecosystemId)?.displayName || ecosystemId;
      for (const project of projects) {
        rows.push({
          ecosystemId,
          projectId: project.id,
          label: `${ecoLabel} / ${project.name || project.id}`,
        });
      }
    }
    return rows.sort((a, b) => a.label.localeCompare(b.label));
  }, [projectsByEco, ecosystemDefs]);

  useEffect(() => {
    if (options.length > 0 && !options.some((o) => `${o.ecosystemId}::${o.projectId}` === selectedKey)) {
      setSelectedKey(`${options[0].ecosystemId}::${options[0].projectId}`);
    }
  }, [options, selectedKey]);

  const [ecosystemId, projectId] = selectedKey.includes('::')
    ? selectedKey.split('::')
    : ['', ''];
  const ecosystemLabel = ecosystemDefs.find((e) => e.id === ecosystemId)?.displayName;

  if (!allowed) {
    return (
      <div className="tree-dashboard-panel">
        <h2>Project Admin</h2>
        <p className="tree-dashboard-muted">You do not have permission to view this section.</p>
      </div>
    );
  }

  return (
    <div className="tree-dashboard-panel">
      <h2>Project Admin</h2>
      <p className="tree-dashboard-muted">
        Manage memberships and sub-projects for projects you maintain. Invite users only into privileges on
        those projects.
      </p>

      <ProjectAdminScopePicker
        options={options}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
      />

      <ProjectIdentityPanel
        expanded={Boolean(expanded.identity)}
        onToggle={() => toggleWidget('identity')}
      />

      <ProjectMembershipPanel
        expanded={Boolean(expanded.memberships)}
        onToggle={() => toggleWidget('memberships')}
        user={user}
        ecosystemId={ecosystemId}
        projectId={projectId}
        ecosystemLabel={ecosystemLabel}
      />
    </div>
  );
}
